/**
 * @file TillSales.js
 * @version 2.19.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-17
 * @modified 2026-11-03
 * @description Till Sales page for Epos Now integration. Sync a day's till transactions, review grouped transactions,
 * and generate HW-D invoices. Epos-customer → account matching now lives in the Sync Contacts screen; sales whose
 * Epos customer isn't mapped are blocked from invoicing, with a clickable banner pointing to Sync Contacts.
 * Invoiced rows whose invoice never reached Xero are flagged "Not synced" and can be re-pushed individually or in bulk.
 * Transactions the server's penny-exact reconciliation gate refused are surfaced with their specific reasons, both in
 * the post-generate dialog and in a persistent panel, since each needs the sale looking at before it can be re-run.
 * A sale billed some other way can be marked invoiced by hand, with a note and optionally the invoice that covered
 * it (linked then or later), from one three-dots row menu mirrored by the right-click menu, singly or in bulk.
 * 2.16.0 (2026-10-07): "Change customer…" on an invoiced row moves its till invoice or credit note to the right
 * customer (here and in Xero — ChangeCustomerModal); the row shows each change with its reason.
 * 2.16.1: a walk-in row moved to an account shows that account's icon and tooltip, not "Walk-in POS customer".
 * 2.17.0 (2026-10-08, Cam): drafts for pricing — a sale that will go to Xero as a draft (customer ticked, Book to
 *   account, a product in 200C/200M/200N/207/207A) is marked before it is generated; its invoice shows as a draft
 *   awaiting pricing; the result says which went as drafts (priced in Price Orders) and which could not.
 *
 * 2.19.0 (2026-11-03, Cam — a till invoice booked to account may be edited
 * in this system): a sale whose invoice was edited here says so under the
 * customer ("HW-D…: edited here — no longer matches this sale") and in the
 * invoiced mark's tip (`invoiceEditedHere` from the server).
 * 2.18.0 (2026-10-15, Cam): invoicing sales dated in a month that should be
 * locked in Xero by now asks first (409 PRIOR_MONTH_CONFIRM before anything
 * streams).
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TillSales.css';
import './TableStyles.css';
import Dropdown from './Dropdown';
import Input from './Input';
import DateInput from './DateInput';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import AlertDialog from './AlertDialog';
import CustomIcon from './CustomIcon';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { SALES_TAX_OPTIONS, PURCHASE_TAX_OPTIONS } from '../utils/taxTypes';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import { useDragSelect } from '../hooks/useDragSelect';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import SearchHelp from './SearchHelp';
import ReviewFlagsPanel from './ReviewFlagsPanel';
import InvoiceModal from './InvoiceModal';
import { revealInSearch } from '../utils/revealInSearch';
import EntityLink from './EntityLink';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import RowActionsMenu from './RowActionsMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import TableContextMenu from './TableContextMenu';
import AuditHistoryModal from './AuditHistoryModal';
import TillInvoiceActionModal from './TillInvoiceActionModal';
import ChangeCustomerModal from './ChangeCustomerModal';
import usePriorMonthConfirm, { isPriorMonthConfirm, PRIOR_MONTH_CONFIRMED } from '../hooks/usePriorMonthConfirm';
import { describeCustomerChange, latestCustomerChange } from '../utils/customerChange';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

function TillSales({ user, onNavigate }) {
  // Till Sales only ever covers CLOSED trading days between go-live and
  // yesterday. Transactions are fetched the morning after the day closes, so
  // today holds nothing yet, and nothing exists before 10/04/2026 — the
  // pre-go-live data was removed and is not fetched again.
  const TILL_GO_LIVE = '2026-04-10';
  const londonToday = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
  const yesterday = new Date(londonToday);
  yesterday.setDate(yesterday.getDate() - 1);
  const maxDate = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  const today = maxDate; // default landing day
  // Date and filters/sort are persisted across page navigation via the shared
  // usePersistedState hook (sessionStorage-backed, namespaced `tillSales.*`).
  // Loading flags, modals, and Set-based selections stay ephemeral on purpose.
  const [selectedDate, setSelectedDate] = usePersistedState('tillSales.selectedDate', today);
  const [groups, setGroups] = useState([]);
  // Count for the whole day regardless of the invoiced filter, so a fully
  // processed day can be told apart from one that was never synced.
  const [transactionsOnDate, setTransactionsOnDate] = useState(0);
  // Bumped after a generate run so any issue it created surfaces without a reload.
  const [flagsRefresh, setFlagsRefresh] = useState(0);
  // Invoice opened from a till transaction's invoice badge.
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  // "Dated in a month that should be locked" — asked before invoicing such sales.
  const priorMonth = usePriorMonthConfirm();
  const [generateProgress, setGenerateProgress] = useState(0);
  const [generateMessage, setGenerateMessage] = useState('');
  const [adminSyncing, setAdminSyncing] = useState(false);
  const [adminSyncProgress, setAdminSyncProgress] = useState(0);
  const [adminSyncMessage, setAdminSyncMessage] = useState('');

  // Filters
  const [showInvoiced, setShowInvoiced] = usePersistedState('tillSales.showInvoiced', false);
  const [tenderFilter, setTenderFilter] = usePersistedState('tillSales.tenderFilter', '');
  const [tenderTypes, setTenderTypes] = useState([]);
  const [searchFilter, setSearchFilter] = usePersistedState('tillSales.searchFilter', '');
  const [matchFilter, setMatchFilter] = usePersistedState('tillSales.matchFilter', '');
  // Any filter on lights the Clear Filters button: the filters above, against
  // what that button resets them to. The selected date is the day being viewed,
  // not a filter, and Clear Filters leaves it alone.
  const filtersApplied = activeHeaderFilters(
    { showInvoiced, tender: tenderFilter, search: searchFilter, match: matchFilter },
    { showInvoiced: false, tender: '', search: '', match: '' }
  ).count > 0;

  // Selection
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [groupLineItems, setGroupLineItems] = useState({});

  // Sorting
  const [sortField, setSortField] = usePersistedState('tillSales.sortField', 'timestamp');
  const [sortDirection, setSortDirection] = usePersistedState('tillSales.sortDirection', 'asc');

  // Bulk actions
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);


  // Transaction detail modal
  const [viewingGroup, setViewingGroup] = useState(null);
  const [viewingLineItems, setViewingLineItems] = useState([]);

  // Product link resolution
  const [linkingProduct, setLinkingProduct] = useState(null); // { eposProductId, productName }
  const [inventoryResults, setInventoryResults] = useState([]);
  const [allInventoryItems, setAllInventoryItems] = useState(null); // cached full list
  const [creatingItem, setCreatingItem] = useState(false);
  const [newItemForm, setNewItemForm] = useState(null); // pre-filled form data
  const [savingNewItem, setSavingNewItem] = useState(false);
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);

  // Re-pushing invoices that were created locally but never reached Xero
  const [repushing, setRepushing] = useState(false);
  const [repushingKey, setRepushingKey] = useState(null); // groupKey of the single row being retried
  // Across ALL dates, not just the day on screen — a stuck invoice from last
  // week is precisely the one nobody would think to look for.
  const [unsyncedCount, setUnsyncedCount] = useState(0);

  // Transactions the server REFUSED to invoice because they don't reconcile to
  // the till receipt to the penny. Kept in state (not just flashed in an alert)
  // because each one needs investigating at the till, and the reasons are the
  // whole point — a bare "3 errors" count tells nobody what to go and look at.
  const [refusals, setRefusals] = useState([]);

  // Alert
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });

  // Manual invoicing: { mode: 'mark'|'link'|'unlink'|'unmark', groups } —
  // one row or a bulk selection. See TillInvoiceActionModal.
  const [invoiceAction, setInvoiceAction] = useState(null);
  // The row whose audit history is open.
  const [historyGroup, setHistoryGroup] = useState(null);
  // The invoice (id) of a row whose customer is being changed (ChangeCustomerModal).
  const [changingCustomerInvoiceId, setChangingCustomerInvoiceId] = useState(null);

  const eventSourceRef = useRef(null);

  // Slideout button sizing
  useSlideOutButton([bulkActionsVisible, syncing, generating, repushing, unsyncedCount, selectedGroups.size, linkingProduct, creatingItem, newItemForm, savingNewItem, viewingGroup]);

  const recalcSlideouts = () => {
    requestAnimationFrame(() => {
      document.querySelectorAll('.btn-slideout').forEach(button => {
        const label = button.querySelector('.btn-label');
        if (!label) return;
        const badge = button.querySelector('.btn-badge');
        const labelWidth = label.offsetWidth;
        const badgeSpace = badge ? 24 : 0;
        const expandedWidth = 45 + labelWidth + badgeSpace + 12;
        button.style.setProperty('--expanded-width', `${expandedWidth}px`);
      });
    });
  };

  // Load on mount
  useEffect(() => {
    loadTenderTypes();
    loadAccountCodeLists();
    loadUnsyncedCount();
  }, []);

  const loadUnsyncedCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/epos-now/invoices/unsynced`, { credentials: 'include' });
      const data = await res.json();
      setUnsyncedCount(data.count || 0);
    } catch (e) {
      console.error('Failed to fetch unsynced invoice count:', e);
    }
  };

  const goToSyncContacts = () => { if (onNavigate) onNavigate('sync-contacts'); };

  // Hand the user to Search Invoices pre-filtered to one document, so they can
  // perform any action available there (utils/revealInSearch.js), with every
  // filter that could hide it cleared.
  const openInSearchInvoices = (invoice) => {
    if (!invoice?.invoice_no || !onNavigate) return;
    revealInSearch(onNavigate, 'search-invoices', invoice.invoice_no, () => {
      setViewingInvoiceId(null);
      setViewingGroup(null);
    }, {
      filters: {
        status: 'all',
        type: 'all',
        productRange: [],
        datePreset: 'all-time',
        dateFrom: '',
        dateTo: '',
        page: 1,
        limit: '20',
        showVoid: true, // the document may be voided — never hide what we sent them to see
        includeAdditives: true
      }
    });
  };

  const loadAccountCodeLists = async () => {
    try {
      const [salesRes, purchRes] = await Promise.all([
        fetch(`${API_URL}/api/inventory/sales-accounts`, { credentials: 'include' }),
        fetch(`${API_URL}/api/inventory/purchase-accounts`, { credentials: 'include' })
      ]);
      const salesData = await salesRes.json();
      const purchData = await purchRes.json();
      // The endpoints now return [{ code, name }] sourced from the cached Xero
      // chart of accounts. Tolerate the old bare-string shape so a stale server
      // during a rolling deploy doesn't blank the dropdowns.
      const normalise = (list) => (list || []).map(a => (typeof a === 'string' ? { code: a, name: null } : a));
      setSalesAccounts(normalise(salesData.accounts));
      setPurchaseAccounts(normalise(purchData.accounts));
    } catch (e) {
      console.error('Failed to fetch account codes:', e);
    }
  };

  // Reload transactions when date or filters change
  // A date persisted before these bounds existed (or simply left open
  // overnight) can fall outside the range — pull it back rather than firing a
  // fetch for a day that cannot hold anything.
  useEffect(() => {
    if (selectedDate < TILL_GO_LIVE) setSelectedDate(TILL_GO_LIVE);
    else if (selectedDate > maxDate) setSelectedDate(maxDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, maxDate]);

  useEffect(() => {
    if (selectedDate) {
      const timeoutId = setTimeout(() => loadTransactions(), searchFilter ? 300 : 0);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedDate, showInvoiced, tenderFilter, searchFilter]);

  // Show/hide bulk actions bar
  useEffect(() => {
    if (selectedGroups.size > 0 && !bulkActionsVisible) {
      setBulkActionsVisible(true);
      setBulkActionsClosing(false);
    } else if (selectedGroups.size === 0 && bulkActionsVisible) {
      setBulkActionsClosing(true);
      setTimeout(() => { setBulkActionsVisible(false); setBulkActionsClosing(false); }, 250);
    }
  }, [selectedGroups.size]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, []);

  // ========================================
  // API Calls
  // ========================================

  const loadTenderTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/epos-now/tender-types`, { credentials: 'include' });
      const data = await res.json();
      setTenderTypes(data.tenderTypes || []);
    } catch (err) {
      console.error('Failed to load tender types:', err);
    }
  };

  // Responses come back out of order; only the newest may write the rows.
  const latest = useLatestRequest();

  const loadTransactions = async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date: selectedDate,
        invoiced: showInvoiced ? 'true' : 'false',
        ...(tenderFilter ? { tender: tenderFilter } : {}),
        ...(searchFilter ? { search: searchFilter } : {})
      });
      const res = await fetch(`${API_URL}/api/epos-now/transactions?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      setGroups(data.groups || []);
      setTransactionsOnDate(data.transactionsOnDate ?? 0);
      setSelectedGroups(new Set());
      // Keep the header's unsynced badge honest — every mutation path
      // (generate, re-push, sync day) refreshes through here.
      loadUnsyncedCount();
    } catch (err) {
      if (!latest.isLatest(seq)) return;
      console.error('Failed to load transactions:', err);
      setAlertDialog({ isOpen: true, message: `Failed to load transactions: ${err.message}`, type: 'error' });
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
  };

  const syncDay = () => {
    if (syncing) return;
    setSyncing(true);
    setSyncProgress(5);
    setSyncMessage('Connecting to Epos Now...');

    const es = new EventSource(`${API_URL}/api/epos-now/sync/day?date=${selectedDate}`, { withCredentials: true });
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          const msg = data.message || 'Syncing...';
          setSyncMessage(msg);
          // Estimate progress from message content
          if (msg.includes('Finding')) setSyncProgress(10);
          else if (msg.includes('Scanning')) setSyncProgress(30);
          else if (msg.includes('Stored')) {
            const match = msg.match(/Stored (\d+)/);
            const added = match ? parseInt(match[1]) : 0;
            setSyncProgress(Math.min(90, 40 + added));
          } else setSyncProgress(prev => Math.min(90, prev + 5));
        } else if (data.type === 'complete') {
          const r = data.result;
          setSyncProgress(100);
          setSyncMessage('Sync complete!');
          setTimeout(() => {
            setSyncing(false);
            setSyncProgress(0);
            setSyncMessage('');
          }, 1000);
          es.close();
          loadTransactions();
          // A sale deleted in Epos Now whose invoice Xero did not void stays
          // live on both sides until someone voids it: say so here, not only
          // in a progress line that is gone a second later.
          const held = Number(r.invoicesBlocked || 0);
          setAlertDialog({
            isOpen: true,
            message: `Synced ${r.added} new transaction${r.added !== 1 ? 's' : ''} (${r.skipped} already existed)`
              + (held > 0 ? `. ${held} invoice${held !== 1 ? 's' : ''} for sales deleted in Epos Now could not be voided — void ${held !== 1 ? 'them' : 'it'} by hand (the reason is in ${held !== 1 ? 'their' : 'its'} notes).` : ''),
            type: held > 0 ? 'warning' : 'success'
          });
        } else if (data.type === 'error') {
          setSyncProgress(0);
          setSyncMessage('');
          setSyncing(false);
          es.close();
          setAlertDialog({ isOpen: true, message: data.error, type: 'error' });
        }
      } catch (e) {
        console.error('SSE parse error:', e);
      }
    };

    es.onerror = () => { setSyncProgress(0); setSyncMessage(''); setSyncing(false); es.close(); };
  };

  // Admin-only full refresh of reference data from Epos Now (products, customers, etc.)
  const runAdminSync = (endpoint, label) => {
    if (adminSyncing) return;
    setAdminSyncing(true);
    setAdminSyncProgress(5);
    setAdminSyncMessage(`Starting ${label}...`);

    const es = new EventSource(`${API_URL}/api/epos-now/sync/${endpoint}`, { withCredentials: true });

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          const msg = data.message || `${label}: page ${data.page || ''}`.trim();
          setAdminSyncMessage(msg);
          // Rough progress estimate
          if (data.current && data.total) {
            setAdminSyncProgress(Math.min(95, Math.round((data.current / data.total) * 100)));
          } else {
            setAdminSyncProgress(prev => Math.min(90, prev + 3));
          }
        } else if (data.type === 'complete') {
          const r = data.result || {};
          setAdminSyncProgress(100);
          setAdminSyncMessage(`${label} complete`);
          setTimeout(() => {
            setAdminSyncing(false);
            setAdminSyncProgress(0);
            setAdminSyncMessage('');
          }, 1000);
          es.close();
          const parts = [];
          if (r.added != null) parts.push(`${r.added} added`);
          if (r.updated != null) parts.push(`${r.updated} updated`);
          if (r.total != null) parts.push(`${r.total} total`);
          setAlertDialog({
            isOpen: true,
            message: `${label} complete${parts.length ? `: ${parts.join(', ')}` : ''}`,
            type: 'success'
          });
        } else if (data.type === 'error') {
          es.close();
          setAdminSyncing(false);
          setAdminSyncProgress(0);
          setAdminSyncMessage('');
          setAlertDialog({ isOpen: true, message: `${label} failed: ${data.error}`, type: 'error' });
        }
      } catch (e) {
        console.error('Failed to parse admin sync event:', e);
      }
    };

    es.onerror = () => {
      setAdminSyncProgress(0);
      setAdminSyncMessage('');
      setAdminSyncing(false);
      es.close();
    };
  };

  // ========================================
  // Expandable Rows
  // ========================================

  const loadLineItems = async (group, { force = false } = {}) => {
    if (!force && groupLineItems[group.groupKey]) return;
    const allItems = [];
    for (const txnId of group.transactionIds) {
      try {
        const res = await fetch(`${API_URL}/api/epos-now/transactions/${txnId}/items`, { credentials: 'include' });
        const data = await res.json();
        allItems.push(...(data.items || []));
      } catch (e) {
        console.error('Failed to load items:', e);
      }
    }
    setGroupLineItems(prev => ({ ...prev, [group.groupKey]: allItems }));
  };

  // Refresh transactions and any currently expanded groups' line items so
  // post-link / post-create changes show without the user having to re-expand.
  const refreshAfterLink = async () => {
    await loadTransactions();
    setGroupLineItems({});
    // Re-fetch line items for groups that were expanded before the refresh.
    // We read expandedRows + groups from the latest committed state via the
    // closures below; loadTransactions() awaited above so groups is fresh.
    const expandedKeys = Array.from(expandedRows);
    if (expandedKeys.length > 0) {
      // Use the freshly-set groups list — accessed via the state updater pattern
      // by reading from the current `groups` after setGroups in loadTransactions
      // already ran (React batches but loadTransactions awaits the fetch).
      setGroups(currentGroups => {
        expandedKeys.forEach(key => {
          const g = currentGroups.find(x => x.groupKey === key);
          if (g) loadLineItems(g, { force: true });
        });
        return currentGroups;
      });
    }
  };

  const toggleRowExpand = async (groupKey) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
      const group = groups.find(g => g.groupKey === groupKey);
      if (group) await loadLineItems(group);
    }
    setExpandedRows(newExpanded);
  };

  const [expandingAll, setExpandingAll] = useState(false);
  const handleExpandAll = async () => {
    if (expandedRows.size === groups.length && groups.length > 0) {
      setExpandedRows(new Set());
      return;
    }
    setExpandingAll(true);
    const allKeys = new Set(groups.map(g => g.groupKey));
    // Load all line items in parallel
    await Promise.all(filteredGroups.map(g => loadLineItems(g)));
    setExpandedRows(allKeys);
    setExpandingAll(false);
  };

  // ========================================
  // Selection
  // ========================================

  const toggleSelectGroup = (groupKey) => {
    setSelectedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) newSet.delete(groupKey);
      else newSet.add(groupKey);
      return newSet;
    });
  };

  const dragSelect = useDragSelect({
    setChecked: (groupKey, checked) => {
      setSelectedGroups(prev => {
        const next = new Set(prev);
        if (checked) next.add(groupKey); else next.delete(groupKey);
        return next;
      });
    }
  });

  // Every visible row, invoiced ones included — they can be linked or unmarked
  // in bulk. Each bulk action acts on the rows it applies to and says so.
  const toggleSelectAll = () => {
    if (filteredGroups.length > 0 && selectedGroups.size === filteredGroups.length) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(filteredGroups.map(g => g.groupKey)));
    }
  };

  // ========================================
  // Contact Resolution
  // ========================================

  // ========================================
  // Product-Inventory Linking
  // ========================================

  const loadAllInventoryItems = async () => {
    if (allInventoryItems) return allInventoryItems;
    try {
      const res = await fetch(`${API_URL}/api/epos-now/inventory/search?search=*`, { credentials: 'include' });
      const data = await res.json();
      const items = data.items || [];
      setAllInventoryItems(items);
      return items;
    } catch (e) {
      console.error('Failed to load inventory:', e);
      return [];
    }
  };

  const openProductLinker = async (item) => {
    setLinkingProduct({ eposProductId: item.epos_product_id, productName: item.product_name });
    const items = await loadAllInventoryItems();
    setInventoryResults(items);
  };

  const openCreateItemForm = async (eposProductId) => {
    setCreatingItem(true);
    recalcSlideouts();
    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/${eposProductId}/suggested-account`, { credentials: 'include' });
      const data = await res.json();
      // Default is_purchased true when Epos gave us a cost price or linked a supplier
      const hasPurchaseSignal = !!(data.grossCostPrice > 0 || data.eposSupplierId);
      setNewItemForm({
        item_code: String(eposProductId),
        item_name: data.productName || '',
        description: data.description || data.productName || '',
        purchase_description: data.description || '',
        is_sold: true,
        is_purchased: hasPurchaseSignal,
        is_tracked_as_inventory: false,
        sales_account_code: data.suggestedAccountCode || '200',
        sales_tax_type: data.suggestedTaxType || 'ZERORATEDOUTPUT',
        sales_unit_price: data.salePrice || '',
        purchase_account_code: data.suggestedPurchaseAccountCode || '310',
        purchase_tax_type: data.suggestedPurchaseTaxType || 'INPUT2',
        purchase_unit_price: data.costPrice || '',
        cogs_account_code: '',
        // Display-only context
        categoryName: data.categoryName || '',
        parentCategoryName: data.parentCategoryName || '',
        eposSupplierName: data.eposSupplierName || '',
        accountCodeMatched: !!data.accountCodeMatched,
        taxGroupMatched: !!data.taxGroupMatched
      });
    } catch (e) {
      setNewItemForm({
        item_code: String(eposProductId),
        item_name: linkingProduct?.productName || '',
        description: '',
        purchase_description: '',
        is_sold: true,
        is_purchased: false,
        is_tracked_as_inventory: false,
        sales_account_code: '200',
        sales_tax_type: 'ZERORATEDOUTPUT',
        sales_unit_price: '',
        purchase_account_code: '310',
        purchase_tax_type: 'INPUT2',
        purchase_unit_price: '',
        cogs_account_code: ''
      });
    }
  };

  const saveNewItemAndLink = async () => {
    if (!linkingProduct || !newItemForm) return;
    if (!newItemForm.item_code || !newItemForm.item_name) {
      setAlertDialog({ isOpen: true, message: 'Item code and name are required', type: 'warning' });
      return;
    }
    setSavingNewItem(true);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/${linkingProduct.eposProductId}/create-and-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newItemForm)
      });
      const data = await res.json();
      if (data.success) {
        const msg = data.alreadyExisted
          ? `Linked to existing item ${data.linkedItem.item_code}`
          : `Created ${data.linkedItem.item_code} - ${data.linkedItem.item_name} and linked`;
        setAlertDialog({ isOpen: true, message: msg, type: 'success' });
        setLinkingProduct(null);
        setCreatingItem(false);
        setNewItemForm(null);
        await refreshAfterLink();
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to create item', type: 'error' });
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setSavingNewItem(false);
    }
  };

  const unlinkProduct = async (eposProductId) => {
    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/${eposProductId}/link-inventory`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(data.error || `Unlink failed (HTTP ${res.status})`);
      }
      setAlertDialog({ isOpen: true, message: 'Product unlinked', type: 'success' });
      await refreshAfterLink();
      // Refresh the modal's view if it's open
      if (viewingGroup) {
        const allItems = [];
        for (const txnId of viewingGroup.transactionIds) {
          try {
            const r = await fetch(`${API_URL}/api/epos-now/transactions/${txnId}/items`, { credentials: 'include' });
            const d = await r.json();
            allItems.push(...(d.items || []));
          } catch (_) { /* ignore */ }
        }
        setViewingLineItems(allItems);
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: `Unlink failed: ${e.message}`, type: 'error' });
    }
  };

  const linkProductToInventory = async (inventoryItemId) => {
    if (!linkingProduct) return;
    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/${linkingProduct.eposProductId}/link-inventory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inventoryItemId })
      });
      const data = await res.json();
      if (data.success) {
        setAlertDialog({ isOpen: true, message: `Linked "${linkingProduct.productName}" to ${data.linkedItem.item_code} - ${data.linkedItem.item_name}`, type: 'success' });
        setLinkingProduct(null);
        await refreshAfterLink();
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to link product', type: 'error' });
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    }
  };

  // ========================================
  // Transaction Detail Modal
  // ========================================

  const openTransactionDetail = async (group) => {
    setViewingGroup(group);
    // Load line items for all transactions in the group
    const allItems = [];
    for (const txnId of group.transactionIds) {
      try {
        const res = await fetch(`${API_URL}/api/epos-now/transactions/${txnId}/items`, { credentials: 'include' });
        const data = await res.json();
        allItems.push(...(data.items || []));
      } catch (e) {
        console.error('Failed to load items:', e);
      }
    }
    setViewingLineItems(allItems);
  };

  // ========================================
  // Invoice Generation
  // ========================================

  // A reconciliation refusal is not a failure in the "something broke" sense —
  // it is the gate doing its job. Every figure on a Till Sales invoice has to
  // agree with the originating till transaction to the penny, because the
  // customer has usually already paid and is holding the receipt, and because
  // per-line VAT feeds the VAT return. When it can't, the server creates
  // nothing and marks nothing invoiced; it hands back the specific reasons so
  // the sale can be looked at and re-run.
  const collectRefusals = (finalResult) =>
    (finalResult?.errors || [])
      .filter(e => e && e.blocked === 'reconciliation')
      .map(e => ({
        groupKey: e.group,
        customerName: e.customerName || 'POS Customer',
        reasons: (Array.isArray(e.reasons) && e.reasons.length > 0)
          ? e.reasons
          : [{ code: 'unknown', message: e.error }]
      }));

  const refusalAlertMessage = (list, generatedCount = null, xeroFailures = []) => (
    <div className="till-refusal-alert">
      {generatedCount !== null && (
        <p>Generated {generatedCount} invoice{generatedCount !== 1 ? 's' : ''}.</p>
      )}
      {xeroFailures.length > 0 && (
        <p>
          {xeroFailures.length} of those created locally but failed to push to Xero — those rows are
          flagged "Not synced"; use Retry Failed Pushes once the cause is fixed.
        </p>
      )}
      <p>
        {list.length} till transaction{list.length !== 1 ? 's were' : ' was'} <strong>not invoiced</strong> because
        {list.length !== 1 ? ' they do' : ' it does'} not reconcile to the till receipt to the penny.
        Nothing was created and nothing was marked invoiced, so {list.length !== 1 ? 'they' : 'it'} can be
        re-run once the sale is corrected.
      </p>
      {list.map((r) => (
        <div key={r.groupKey} className="till-refusal-item">
          <strong>{r.customerName}</strong>
          <ul>
            {r.reasons.map((x, i) => <li key={i}>{x.message}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );

  /**
   * Send; when the server asks about a month that should be locked in Xero
   * (409 PRIOR_MONTH_CONFIRM, answered before anything streams), ask, and
   * send again confirmed. Null when the person went back.
   */
  const askPriorMonthThen = async (post) => {
    const res = await post({});
    if (res.status !== 409) return res;
    const raw = await res.text();
    let body = null;
    try { body = JSON.parse(raw); } catch (_) { body = null; }
    if (isPriorMonthConfirm(body)) {
      if (!(await priorMonth.ask(body))) return null;
      return post(PRIOR_MONTH_CONFIRMED);
    }
    throw new Error((body && body.error) || `${res.status} ${res.statusText}`.trim());
  };

  const generateSingleInvoice = async (group) => {
    if (group.contactMatchStatus === 'unmatched') {
      setAlertDialog({ isOpen: true, message: 'This Epos customer isn\'t mapped to an account. Map it in Sync Contacts before invoicing.', type: 'warning' });
      return;
    }
    if (group.unmatchedProducts > 0) {
      setAlertDialog({ isOpen: true, message: `${group.unmatchedProducts} product(s) not matched to inventory. A default account code will be used.`, type: 'info' });
    }
    setGenerating(true);
    setRefusals([]);
    try {
      const post = (extra) => fetch(`${API_URL}/api/epos-now/generate-invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ groups: [group], ...extra })
      });
      // A sale dated in a month that should be locked in Xero by now: asked
      // first (Cam, 15 Oct 2026); Go back invoices nothing.
      const res = await askPriorMonthThen(post);
      if (!res) return;

      if (!res.ok && res.headers.get('content-type')?.includes('application/json')) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to generate invoice');
      }

      // Read NDJSON stream (same format as bulk)
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;
      let streamError = null;
      const xeroFailures = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line || !line.startsWith('{')) continue;
          let msg;
          try { msg = JSON.parse(line); } catch (_) { continue; }
          if (msg.type === 'result') finalResult = msg;
          else if (msg.type === 'error') streamError = msg.error;
          else if (msg.type === 'group_result' && msg.xeroSynced === false) {
            xeroFailures.push(msg);
          }
        }
      }
      if (buffer.trim().startsWith('{')) {
        try {
          const msg = JSON.parse(buffer.trim());
          if (msg.type === 'result') finalResult = msg;
          else if (msg.type === 'error') streamError = msg.error;
          else if (msg.type === 'group_result' && msg.xeroSynced === false) {
            xeroFailures.push(msg);
          }
        } catch (_) { /* ignore */ }
      }

      if (streamError) throw new Error(streamError);

      if (finalResult?.success && finalResult.invoicesCreated?.length > 0) {
        const inv = finalResult.invoicesCreated[0];
        if (xeroFailures.length > 0) {
          const reason = xeroFailures[0].error || 'Unknown';
          setAlertDialog({
            isOpen: true,
            type: 'warning',
            message: `Generated ${inv.invoiceNo} for ${inv.customerName} (${formatCurrency(inv.total)}) locally, but the Xero push failed: ${reason}. The row is flagged "Not synced" — fix the cause, then use the re-push button on that row.`,
          });
        } else {
          setAlertDialog({ isOpen: true, message: `Generated ${inv.invoiceNo} for ${inv.customerName} (${formatCurrency(inv.total)})${draftsLine(finalResult.invoicesCreated)}`, type: 'success' });
        }
        loadTransactions();
      } else {
        const refused = collectRefusals(finalResult);
        if (refused.length > 0) {
          setRefusals(refused);
          setAlertDialog({ isOpen: true, type: 'warning', message: refusalAlertMessage(refused) });
        } else {
          const errMsg = finalResult?.errors?.length > 0 ? finalResult.errors[0].error : (finalResult?.error || 'Failed to generate invoice');
          setAlertDialog({ isOpen: true, message: errMsg, type: 'error' });
        }
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setGenerating(false);
      setFlagsRefresh(n => n + 1);
    }
  };

  const generateInvoices = async () => {
    const selected = groups.filter(g => selectedGroups.has(g.groupKey) && !g.invoiced);
    const unmatched = selected.filter(g => g.contactMatchStatus === 'unmatched');
    if (unmatched.length > 0) {
      setAlertDialog({ isOpen: true, message: `${unmatched.length} selected transaction${unmatched.length > 1 ? 's have' : ' has'} an unmapped Epos customer. Map them in Sync Contacts first.`, type: 'warning' });
      return;
    }
    if (selected.length === 0) {
      setAlertDialog({ isOpen: true, message: 'None of the selected transactions is waiting to be invoiced', type: 'warning' });
      return;
    }

    setGenerating(true);
    setRefusals([]);
    setGenerateProgress(0);
    setGenerateMessage('Starting...');
    try {
      const post = (extra) => fetch(`${API_URL}/api/epos-now/generate-invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        /* Only the fields generateHWInvoices actually reads. The rest of a
           group — every line item, product name and price — is re-read from
           the database server-side anyway ("never trust the client-supplied
           group"), and sending it put a full day past the body-parser limit,
           which failed the whole run before it started. */
        body: JSON.stringify({
          groups: selected.map(g => ({
            groupKey: g.groupKey,
            transactionIds: g.transactionIds,
            customerName: g.customerName,
            timestamp: g.timestamp,
            invoiced: g.invoiced
          })),
          ...extra
        })
      });
      // Sales dated in a month that should be locked in Xero by now: one
      // question naming them (Cam, 15 Oct 2026); Go back invoices nothing.
      const res = await askPriorMonthThen(post);
      if (!res) return;

      if (!res.ok) {
        // Not every failure is JSON: a body-parser rejection or a proxy error
        // arrives as HTML, and the old check skipped those and then tried to
        // read the error page as a progress stream — which is how a 413 was
        // reported to the user as "Internal Server Error".
        const raw = await res.text();
        let detail = '';
        try { detail = JSON.parse(raw).error || ''; } catch (_) { detail = ''; }
        if (!detail) {
          detail = res.status === 413
            ? 'The request was too large for the server. Select fewer transactions and try again.'
            : `${res.status} ${res.statusText}`.trim();
        }
        throw new Error(`Failed to generate invoices — ${detail}`);
      }

      // Read NDJSON stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;
      let streamError = null;
      const xeroFailures = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep the trailing partial line
        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) continue;
          // Only attempt to parse lines that look like JSON objects
          if (!line.startsWith('{')) {
            console.warn('Skipping non-JSON line from stream:', line);
            continue;
          }
          let msg;
          try {
            msg = JSON.parse(line);
          } catch (parseErr) {
            console.warn('Failed to parse stream line:', line, parseErr.message);
            continue;
          }
          if (msg.type === 'progress') {
            setGenerateProgress(Math.min(99, msg.percentage || 0));
            setGenerateMessage(msg.message || 'Processing...');
          } else if (msg.type === 'complete') {
            setGenerateProgress(100);
            setGenerateMessage(msg.message || 'Complete');
          } else if (msg.type === 'result') {
            finalResult = msg;
          } else if (msg.type === 'error') {
            streamError = msg.error || 'Unknown error from server';
          } else if (msg.type === 'group_result' && msg.xeroSynced === false) {
            xeroFailures.push(msg);
          }
        }
      }

      // Handle any leftover buffer content (final line without trailing newline)
      if (buffer.trim().startsWith('{')) {
        try {
          const msg = JSON.parse(buffer.trim());
          if (msg.type === 'result') finalResult = msg;
          else if (msg.type === 'error') streamError = msg.error;
          else if (msg.type === 'group_result' && msg.xeroSynced === false) {
            xeroFailures.push(msg);
          }
        } catch (_) { /* ignore */ }
      }

      if (streamError) {
        throw new Error(streamError);
      }

      if (finalResult && finalResult.success) {
        const count = finalResult.invoicesCreated?.length || 0;
        const errors = finalResult.errors?.length || 0;

        // Reconciliation refusals get their own surface. Rolling them into the
        // "(N errors)" count was the previous behaviour and it hid the only
        // thing worth knowing: which sale disagreed with its receipt, and how.
        const refused = collectRefusals(finalResult);
        if (refused.length > 0) {
          setRefusals(refused);
          setAlertDialog({ isOpen: true, type: 'warning', message: refusalAlertMessage(refused, count, xeroFailures) });
          loadTransactions();
          return;
        }

        let msg = `Generated ${count} invoice${count !== 1 ? 's' : ''}`;
        if (errors > 0) msg += ` (${errors} error${errors > 1 ? 's' : ''})`;
        msg += draftsLine(finalResult.invoicesCreated);
        if (xeroFailures.length > 0) {
          const sample = xeroFailures.slice(0, 3).map(f => `• ${f.invoiceNo}: ${f.error || 'Unknown'}`).join('\n');
          const more = xeroFailures.length > 3 ? `\n…and ${xeroFailures.length - 3} more` : '';
          msg += `\n\n${xeroFailures.length} of ${count} created locally but failed to push to Xero. Those rows are flagged "Not synced" — fix the cause, then use Retry Failed Pushes:\n${sample}${more}`;
          setAlertDialog({ isOpen: true, message: msg, type: 'warning' });
        } else {
          setAlertDialog({ isOpen: true, message: msg, type: count > 0 ? 'success' : 'warning' });
        }
        loadTransactions();
      } else if (finalResult) {
        setAlertDialog({ isOpen: true, message: finalResult.error || 'Failed to generate invoices', type: 'error' });
      } else {
        // Stream ended without a final result — but the operation may have still succeeded
        // on the server. Refresh and show a neutral message rather than falsely erroring.
        console.warn('Stream ended without a final result message. Refreshing transactions.');
        setAlertDialog({ isOpen: true, message: 'Invoice generation completed. Refreshing to show current state.', type: 'info' });
        loadTransactions();
      }
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setTimeout(() => {
        setGenerating(false);
        setGenerateProgress(0);
        setGenerateMessage('');
        setFlagsRefresh(n => n + 1);
      }, 800);
    }
  };

  // ========================================
  // Re-push to Xero
  // ========================================
  //
  // An invoice can exist locally but never reach Xero — most often because an
  // item points at an account Xero refuses on a sales invoice, or the customer
  // has no Xero contact. The till transactions are already consumed by then, so
  // the basket is never re-offered; these two handlers are the only way back.

  // Retry one row's invoice.
  const repushInvoice = async (group) => {
    if (!group.invoiceId) return;
    setRepushingKey(group.groupKey);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/invoices/${group.invoiceId}/repush`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Re-push failed');

      if (data.success) {
        setAlertDialog({ isOpen: true, message: `${data.invoiceNo} pushed to Xero.`, type: 'success' });
      } else if (data.skipped) {
        setAlertDialog({ isOpen: true, message: `${data.invoiceNo} skipped: ${data.reason}`, type: 'info' });
      } else {
        setAlertDialog({ isOpen: true, message: `${data.invoiceNo} still failing:\n\n${data.error}`, type: 'error' });
      }
      loadTransactions();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setRepushingKey(null);
    }
  };

  // Retry every unsynced till document, not just the ones on screen — a failure
  // from an earlier date is exactly the kind that gets forgotten.
  const repushAllFailed = async () => {
    setRepushing(true);
    setGenerateProgress(0);
    setGenerateMessage('Starting...');
    try {
      const res = await fetch(`${API_URL}/api/epos-now/invoices/repush-failed`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok && res.headers.get('content-type')?.includes('application/json')) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to re-push invoices');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;
      let streamError = null;

      const handle = (msg) => {
        if (msg.type === 'progress') {
          setGenerateProgress(Math.min(99, msg.percentage || 0));
          setGenerateMessage(msg.message || 'Processing...');
        } else if (msg.type === 'complete') {
          setGenerateProgress(100);
          setGenerateMessage(msg.message || 'Complete');
        } else if (msg.type === 'result') {
          finalResult = msg;
        } else if (msg.type === 'error') {
          streamError = msg.error || 'Unknown error from server';
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line.startsWith('{')) continue;
          try { handle(JSON.parse(line)); } catch (_) { /* ignore partial */ }
        }
      }
      if (buffer.trim().startsWith('{')) {
        try { handle(JSON.parse(buffer.trim())); } catch (_) { /* ignore */ }
      }

      if (streamError) throw new Error(streamError);

      if (finalResult) {
        if (finalResult.total === 0) {
          setAlertDialog({ isOpen: true, message: 'No unsynced Till Sales invoices to re-push.', type: 'info' });
        } else if (finalResult.failed.length > 0) {
          const sample = finalResult.failed.slice(0, 3).map(f => `• ${f.invoiceNo}: ${f.error || 'Unknown'}`).join('\n');
          const more = finalResult.failed.length > 3 ? `\n…and ${finalResult.failed.length - 3} more` : '';
          setAlertDialog({
            isOpen: true,
            message: `Pushed ${finalResult.pushed} of ${finalResult.total}. ${finalResult.failed.length} still failing:\n${sample}${more}`,
            type: 'warning'
          });
        } else {
          setAlertDialog({ isOpen: true, message: `Pushed ${finalResult.pushed} invoice${finalResult.pushed !== 1 ? 's' : ''} to Xero.`, type: 'success' });
        }
      }
      loadTransactions();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'error' });
    } finally {
      setTimeout(() => {
        setRepushing(false);
        setGenerateProgress(0);
        setGenerateMessage('');
      }, 800);
    }
  };

  // ========================================
  // Helpers
  // ========================================

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // An invoiced row whose invoice never reached Xero. A voided invoice is
  // excluded — there is nothing to push, so flagging it would be noise.
  // A manually linked invoice is an ordinary invoice, not the till's own — it
  // is pushed from Search Invoices, never the till (VAT-inclusive) way.
  const needsRepush = (group) =>
    group.invoiced && !group.invoicedManually && !!group.invoiceId
    && !group.xeroSynced && !['VOIDED', 'DELETED'].includes(group.invoiceStatus);

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return `\u00A3${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateDisplay = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Counts are from full (unfiltered) groups
  const unmatchedContactCount = groups.filter(g => !g.invoiced && g.contactMatchStatus === 'unmatched').length;
  const unmatchedProductCount = groups.filter(g => !g.invoiced && g.unmatchedProducts > 0).length;

  // Apply client-side match filter
  const filteredGroups = matchFilter ? groups.filter(g => {
    switch (matchFilter) {
      case 'issues': return g.contactMatchStatus === 'unmatched' || g.unmatchedProducts > 0;
      case 'unmatched_contact': return g.contactMatchStatus === 'unmatched';
      case 'unmatched_product': return g.unmatchedProducts > 0;
      case 'manual_product_match': return (g.manuallyMatchedProducts || 0) > 0;
      case 'matched': return g.contactMatchStatus !== 'unmatched' && g.unmatchedProducts === 0;
      default: return true;
    }
  }) : groups;

  // Client-side sorting
  const handleSort = (field) => {
    if (field === sortField) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };
  const sortedGroups = [...filteredGroups].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'timestamp': aVal = a.timestamp; bVal = b.timestamp; break;
      case 'customerName': aVal = (a.customerName || '').toLowerCase(); bVal = (b.customerName || '').toLowerCase(); break;
      case 'totalGross': aVal = a.totalGross; bVal = b.totalGross; break;
      default: aVal = a.timestamp; bVal = b.timestamp;
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // The selection, split by what each action can do with it.
  const selectedList = sortedGroups.filter(g => selectedGroups.has(g.groupKey));
  const selectedUninvoiced = selectedList.filter(g => !g.invoiced);
  const selectedManual = selectedList.filter(g => g.invoiced && g.invoicedManually);
  const selectedLinkable = selectedList.filter(g => !g.invoiced || g.invoicedManually);
  const hasUnmatchedSelected = selectedUninvoiced.some(g => g.contactMatchStatus === 'unmatched');
  const dayNet = groups.reduce((sum, g) => sum + g.totalNet, 0);
  const dayTax = groups.reduce((sum, g) => sum + g.totalTax, 0);
  const dayGross = groups.reduce((sum, g) => sum + g.totalGross, 0);
  const colCount = 10;

  // ── Manual invoicing + the row menu ─────────────────────────────────────
  const openInvoiceAction = (mode, list) => {
    if (list.length) setInvoiceAction({ mode, groups: list });
  };

  // What came back from a mark/link/unmark: say what changed and, for a bulk
  // action over a mixed selection, what was left alone and why.
  const onInvoiceActionDone = (result, { mode }) => {
    setInvoiceAction(null);
    const verb = { mark: 'marked as invoiced', link: 'linked', unlink: 'unlinked', unmark: 'unmarked as invoiced' }[mode];
    const skipped = Array.isArray(result.skipped) ? result.skipped : [];
    const reasons = [...new Set(skipped.map(s => s.reason))];
    setAlertDialog({
      isOpen: true,
      type: skipped.length && !result.updated ? 'warning' : 'success',
      message: `${result.updated} transaction${result.updated === 1 ? '' : 's'} ${verb}.`
        + (skipped.length ? ` ${skipped.length} left unchanged: ${reasons.join('; ')}.` : '')
    });
    loadTransactions();
    setFlagsRefresh(n => n + 1);
  };

  // What became a draft for pricing, and what could not, for the result message.
  const draftsLine = (created) => {
    const list = created || [];
    const drafts = list.filter(i => i.draftForPricing);
    const notDrafts = list.filter(i => i.draftNote);
    let line = '';
    if (drafts.length === 1 && list.length === 1) line += ' — sent to Xero as a DRAFT, to be priced in Pricing & Costing.';
    else if (drafts.length) line += `\n\n${drafts.length} went to Xero as DRAFTS, to be priced in Pricing & Costing: ${drafts.map(i => i.invoiceNo).join(', ')}.`;
    if (notDrafts.length) line += `\n\n${notDrafts.map(i => `${i.invoiceNo}: ${i.draftNote}`).join('\n')}`;
    return line;
  };

  const invoicedTitle = (group) => {
    if (!group.invoiced) return 'Not invoiced';
    if (!group.invoicedManually && String(group.invoiceStatus || '').toUpperCase() === 'DRAFT') {
      return `${group.invoiceNo || 'Invoice'} — a draft in Xero, awaiting pricing in Pricing & Costing`;
    }
    if (!group.invoicedManually && group.invoiceEditedHere) {
      return `${group.invoiceNo || 'Invoice'} — edited here (booked to account): it no longer matches this sale`;
    }
    if (!group.invoicedManually) return group.invoiceNo || 'Invoiced';
    const who = [group.invoicedByName, group.invoicedAt && formatDateDisplay(String(group.invoicedAt).slice(0, 10))]
      .filter(Boolean).join(', ');
    return `Marked invoiced by hand${who ? ` (${who})` : ''} — ${group.invoiceNo ? `linked to ${group.invoiceNo}` : 'no invoice linked'}`;
  };

  /*
   * One list per row, shared by the three-dots menu and the right-click menu so
   * the two can never disagree. Every action is listed on every row, disabled
   * with a reason where it doesn't apply (CLAUDE.md: disable, never hide).
   */
  const buildRowActions = (group) => {
    const manual = group.invoiced && group.invoicedManually;
    const generated = group.invoiced && !group.invoicedManually;
    return [
      { key: 'view', icon: 'visibility', label: 'View details', readSafe: true, onClick: () => openTransactionDetail(group) },
      {
        key: 'invoice', icon: 'receipt_long', label: group.invoiceNo ? `View invoice ${group.invoiceNo}` : 'View invoice',
        readSafe: true, disabled: !group.invoiceId,
        title: group.invoiceId ? undefined : 'No invoice linked',
        onClick: () => setViewingInvoiceId(group.invoiceId)
      },
      {
        key: 'contact', icon: 'person_search', label: 'Resolve in Sync Contacts', readSafe: true,
        disabled: group.contactMatchStatus !== 'unmatched',
        title: group.contactMatchStatus === 'unmatched' ? undefined : 'Contact already matched',
        onClick: goToSyncContacts
      },
      {
        key: 'generate', icon: 'receipt', label: 'Generate invoice',
        disabled: generating || group.invoiced || group.contactMatchStatus === 'unmatched',
        title: group.invoiced ? invoicedTitle(group)
          : group.contactMatchStatus === 'unmatched' ? 'Map the Epos customer in Sync Contacts first' : undefined,
        onClick: () => generateSingleInvoice(group)
      },
      {
        key: 'mark', icon: 'task_alt', label: 'Mark as invoiced…',
        disabled: group.invoiced,
        title: group.invoiced ? invoicedTitle(group) : 'Already billed some other way — no invoice will be generated',
        onClick: () => openInvoiceAction('mark', [group])
      },
      {
        key: 'link', icon: 'link', label: manual && group.invoiceId ? 'Change linked invoice…' : 'Link invoice…',
        disabled: generated,
        title: generated ? `Invoiced by generating ${group.invoiceNo || 'its own invoice'} — that link cannot be changed` : undefined,
        onClick: () => openInvoiceAction('link', [group])
      },
      {
        key: 'unlink', icon: 'link_off', label: 'Unlink invoice…',
        disabled: !(manual && group.invoiceId),
        title: manual && group.invoiceId ? undefined : (generated ? 'A generated invoice is undone by voiding it' : 'No linked invoice'),
        onClick: () => openInvoiceAction('unlink', [group])
      },
      {
        key: 'unmark', icon: 'remove_done', label: 'Unmark as invoiced…',
        disabled: !manual,
        title: manual ? undefined : (generated ? 'Invoiced by generating an invoice — void it to undo' : 'Not marked as invoiced'),
        onClick: () => openInvoiceAction('unmark', [group])
      },
      {
        key: 'repush', icon: repushingKey === group.groupKey ? 'progress_activity' : 'sync_problem', label: 'Re-push to Xero',
        disabled: !needsRepush(group) || repushing || repushingKey === group.groupKey,
        title: needsRepush(group)
          ? (group.xeroPushError ? `Last error: ${group.xeroPushError}` : undefined)
          : group.invoicedManually ? 'A linked invoice is pushed from Search Invoices'
          : group.invoiced ? 'Already in Xero' : 'Not invoiced yet',
        onClick: () => repushInvoice(group)
      },
      {
        // The till invoice's customer — the one change a till document allows.
        key: 'change-customer', icon: 'swap_horiz', label: 'Change customer…',
        disabled: !group.invoiceId || !!group.customerChangeBlock,
        title: !group.invoiceId ? 'Not invoiced yet'
          : (group.customerChangeBlock || `Move ${group.invoiceNo || 'its invoice'} to the right customer, here and in Xero`),
        onClick: () => setChangingCustomerInvoiceId(group.invoiceId)
      },
      {
        key: 'history', icon: 'history', label: 'View history', readSafe: true,
        onClick: () => setHistoryGroup(group)
      }
    ];
  };

  // Bulk: each action counts the selected rows it applies to and acts on those.
  const buildBulkActions = () => [
    {
      key: 'b-generate', icon: 'receipt', label: `Generate invoices (${selectedUninvoiced.length})`,
      disabled: generating || !selectedUninvoiced.length || hasUnmatchedSelected,
      title: hasUnmatchedSelected ? 'Some selected transactions have an unmapped Epos customer' : undefined,
      onClick: generateInvoices
    },
    {
      key: 'b-mark', icon: 'task_alt', label: `Mark as invoiced… (${selectedUninvoiced.length})`,
      disabled: !selectedUninvoiced.length, onClick: () => openInvoiceAction('mark', selectedUninvoiced)
    },
    {
      key: 'b-link', icon: 'link', label: `Link invoice… (${selectedLinkable.length})`,
      disabled: !selectedLinkable.length, onClick: () => openInvoiceAction('link', selectedLinkable)
    },
    {
      key: 'b-unmark', icon: 'remove_done', label: `Unmark as invoiced… (${selectedManual.length})`,
      disabled: !selectedManual.length, onClick: () => openInvoiceAction('unmark', selectedManual)
    },
    {
      key: 'b-clear', icon: 'close', label: 'Clear selection', readSafe: true,
      onClick: () => setSelectedGroups(new Set())
    }
  ];

  const ctxMenu = useTableContextMenu({
    rows: sortedGroups,
    selectedIds: selectedGroups,
    getRowId: (g) => g.groupKey
  });

  const getTenderIcon = (tender) => {
    const t = (tender || '').toLowerCase();
    if (t.includes('card')) return 'credit_card';
    if (t.includes('cash')) return 'payments';
    if (t.includes('book')) return 'menu_book';
    if (t.includes('voucher')) return 'redeem';
    if (t.includes('cheque')) return 'receipt_long';
    if (t.includes('bacs')) return 'account_balance';
    return 'payment';
  };

  // A mixed-tender sale carries one entry per tender used. Rows synced before
  // per-tender detail was captured fall back to the single tender_summary name.
  const getTenderParts = (group) => (
    group.tenders?.length
      ? group.tenders
      : (group.tenderSummary ? [{ name: group.tenderSummary, amount: null }] : [])
  );

  const getTenderTitle = (parts) => parts
    .map(p => (p.amount != null ? `${p.name}: ${formatCurrency(p.amount)}` : p.name))
    .join(' + ');

  const getContactIcon = (status) => {
    switch (status) {
      case 'pos_customer': return 'storefront';
      case 'auto_matched': return 'check_circle';
      case 'user_confirmed': return 'verified';
      case 'unmatched': return 'warning';
      default: return 'help';
    }
  };

  const getContactIconClass = (status) => {
    switch (status) {
      case 'pos_customer': return 'icon-muted';
      case 'auto_matched': case 'user_confirmed': return 'icon-success';
      case 'unmatched': return 'icon-warning';
      default: return '';
    }
  };

  // ========================================
  // Render
  // ========================================

  return (
    <div className="search-till-sales">
      {/* Header */}
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Sync till transactions from Epos Now, review and generate HW invoices</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Yesterday's takings sync overnight. Resolve any unmatched contacts/ products, and generate invoices. Use 'Sync Day' to re-pull takings.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => {
                  setShowInvoiced(false);
                  setTenderFilter('');
                  setSearchFilter('');
                  setMatchFilter('');
                  setSortField('timestamp');
                  setSortDirection('asc');
                }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <>
                <div>
                  <button
                    onClick={() => runAdminSync('customers', 'Customers sync')}
                    className="btn-slideout admin-item"
                    disabled={adminSyncing}
                    title="Full refresh of all customers from Epos Now (admin)"
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">group</span>
                    </div>
                    <span className="btn-label">Sync Customers</span>
                  </button>
                </div>
                <div>
                  <button
                    onClick={() => runAdminSync('products', 'Products sync')}
                    className="btn-slideout admin-item"
                    disabled={adminSyncing}
                    title="Full refresh of all products from Epos Now (admin)"
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">category</span>
                    </div>
                    <span className="btn-label">Sync Products</span>
                  </button>
                </div>
                <div>
                  <button
                    onClick={() => runAdminSync('reference-data', 'Reference data sync')}
                    className="btn-slideout admin-item"
                    disabled={adminSyncing}
                    title="Refresh categories, tender types, discount reasons (admin)"
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">database</span>
                    </div>
                    <span className="btn-label">Sync Reference</span>
                  </button>
                </div>
              </>
            )}
            <div>
              <button onClick={syncDay} className="btn-slideout" disabled={syncing}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">sync</span>
                </div>
                <span className="btn-label">{syncing ? 'Syncing...' : 'Sync Day'}</span>
              </button>
            </div>
            <div>
              <button
                onClick={generateInvoices}
                className="btn-slideout"
                disabled={generating || selectedUninvoiced.length === 0 || hasUnmatchedSelected}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">receipt</span>
                </div>
                <span className="btn-label">{generating ? 'Generating...' : 'Generate Invoices'}</span>
                {selectedUninvoiced.length > 0 && <span className="btn-badge">{selectedUninvoiced.length}</span>}
              </button>
            </div>
            <div>
              <button
                onClick={repushAllFailed}
                className="btn-slideout"
                disabled={repushing || generating || unsyncedCount === 0}
                title={unsyncedCount === 0
                  ? 'Every Till Sales invoice has reached Xero'
                  : `${unsyncedCount} Till Sales invoice(s) exist locally but not in Xero`}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">sync_problem</span>
                </div>
                <span className="btn-label">{repushing ? 'Re-pushing...' : 'Retry Failed Pushes'}</span>
                {unsyncedCount > 0 && <span className="btn-badge">{unsyncedCount}</span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="search-filters">
        <div className="filter-row till-filter-row">
          <div className="filter-group" style={{ minWidth: '180px' }}>
            <DateInput
              label="Date"
              value={selectedDate}
              min={TILL_GO_LIVE}
              max={maxDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'item code']} />
            </label>
            <Input
              type="text"
              placeholder="Customer, account #, product..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              onEnter={() => { if (selectedDate) loadTransactions(); }}
              icon="search"
            />
          </div>
          <div className={`filter-group ${(unmatchedContactCount > 0 || unmatchedProductCount > 0) && !matchFilter ? 'filter-warning' : ''}`} style={{ minWidth: '160px' }}>
            <label>Match Status</label>
            <Dropdown
              options={[
                { label: 'All', value: '' },
                { label: `Issues (${unmatchedContactCount + unmatchedProductCount})`, value: 'issues' },
                { label: 'Unmatched Contacts', value: 'unmatched_contact' },
                { label: 'Unmatched Products', value: 'unmatched_product' },
                { label: 'Has Manual Product Match', value: 'manual_product_match' },
                { label: 'Matched', value: 'matched' }
              ]}
              value={matchFilter}
              onChange={(val) => setMatchFilter(val)}
              placeholder="All"
            />
          </div>
          <div className="filter-group" style={{ minWidth: '160px' }}>
            <label>Tender</label>
            <Dropdown
              options={[
                { label: 'All Tenders', value: '' },
                ...tenderTypes.map(t => ({ label: t.name, value: t.name }))
              ]}
              value={tenderFilter}
              onChange={(val) => setTenderFilter(val)}
              placeholder="All Tenders"
            />
          </div>
          <div className="filter-group">
            <label>Invoiced</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-invoiced-sales"
                  type="checkbox"
                  checked={showInvoiced}
                  onChange={(e) => setShowInvoiced(e.target.checked)}
                />
                <label htmlFor="show-invoiced-sales"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {generateProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={generateProgress} message={generateMessage} />
        </div>
      )}

      {adminSyncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={adminSyncProgress} message={adminSyncMessage} />
        </div>
      )}

      {/* Bulk Actions Bar */}
      {bulkActionsVisible && (
        <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
          <div className="bulk-actions-info">
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id="bulk-compound-indicator" checked readOnly />
              <label htmlFor="bulk-compound-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedGroups.size} transaction{selectedGroups.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button
                className="btn-slideout"
                onClick={generateInvoices}
                disabled={generating || hasUnmatchedSelected || selectedUninvoiced.length === 0}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">receipt</span>
                </div>
                <span className="btn-label">{generating ? 'Generating...' : 'Generate Invoices'}</span>
              </button>
            </div>
            {/* Manual invoicing — the same actions as the row and right-click
                menus, over the selected rows each one applies to. */}
            <div>
              <button className="btn-slideout" onClick={() => openInvoiceAction('mark', selectedUninvoiced)} disabled={selectedUninvoiced.length === 0}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">task_alt</span>
                </div>
                <span className="btn-label">Mark Invoiced</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => openInvoiceAction('link', selectedLinkable)} disabled={selectedLinkable.length === 0}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">link</span>
                </div>
                <span className="btn-label">Link Invoice</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => openInvoiceAction('unmark', selectedManual)} disabled={selectedManual.length === 0}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">remove_done</span>
                </div>
                <span className="btn-label">Unmark Invoiced</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout" onClick={() => setSelectedGroups(new Set())}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warning Banner */}
      {groups.length > 0 && (unmatchedContactCount > 0 || unmatchedProductCount > 0) && (
        <div className="till-warning-banner">
          <span className="material-symbols-rounded" style={{ fontSize: 16 }}>warning</span>
          <span>
            {unmatchedContactCount > 0 && (
              <button type="button" className="till-warning-link" onClick={goToSyncContacts} title="Resolve in Sync Contacts">
                {unmatchedContactCount} transaction{unmatchedContactCount !== 1 ? 's' : ''} with unmapped Epos customers — resolve in Sync Contacts
                <span className="material-symbols-rounded" style={{ fontSize: 14, verticalAlign: 'middle' }}>chevron_right</span>
              </button>
            )}
            {unmatchedContactCount > 0 && unmatchedProductCount > 0 && ' · '}
            {unmatchedProductCount > 0 && (
              <>{unmatchedProductCount} transaction{unmatchedProductCount !== 1 ? 's' : ''} with unmatched products (default account code will be used)</>
            )}
          </span>
        </div>
      )}

      {/* Transactions the penny-exact gate refused. Persists after the alert is
          dismissed: each one needs someone to go and look at the sale, and the
          reasons are the actionable part. The transactions stay un-invoiced, so
          they also remain in the review-flags panel below until resolved. */}
      {refusals.length > 0 && (
        <div className="till-refusals">
          <div className="till-refusals-header">
            <span className="material-symbols-rounded">rule</span>
            <span>
              {refusals.length} transaction{refusals.length !== 1 ? 's' : ''} not invoiced — {refusals.length !== 1 ? 'they do' : 'it does'} not
              reconcile to the till receipt to the penny
            </span>
            <button
              type="button"
              className="btn-icon till-refusals-dismiss"
              title="Dismiss"
              onClick={() => setRefusals([])}
            >
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          {refusals.map((r) => (
            <div key={r.groupKey} className="till-refusal-row">
              <strong>{r.customerName}</strong>
              <ul>
                {r.reasons.map((x, i) => <li key={i}>{x.message}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* Data-quality flags raised while turning till sales into invoices —
          renders nothing when there is nothing outstanding. */}
      <ReviewFlagsPanel context="till" refreshKey={flagsRefresh} />

      {/* Table Panel */}
      <div className="till-sales-panel">
        <div className="table-toolbar">
          <button
            className="btn-secondary btn-sm"
            onClick={handleExpandAll}
            disabled={loading || expandingAll || filteredGroups.length === 0}
          >
            <span className="material-symbols-rounded">
              {expandedRows.size === filteredGroups.length && filteredGroups.length > 0 ? 'unfold_less' : 'unfold_more'}
            </span>
            {expandingAll ? 'Loading...' : (expandedRows.size === filteredGroups.length && filteredGroups.length > 0 ? 'Collapse All' : 'Expand All')}
          </button>

          <div className="till-stats">
            {filteredGroups.length > 0 ? (
              <>{formatDateDisplay(selectedDate)} &middot; {filteredGroups.length.toLocaleString()} transaction{filteredGroups.length !== 1 ? 's' : ''}{matchFilter ? ` (filtered)` : ''} &middot; NET {formatCurrency(dayNet)} &middot; Tax {formatCurrency(dayTax)} &middot; <strong>Total {formatCurrency(dayGross)}</strong></>
            ) : (
              loading ? 'Loading...' : 'No transactions found'
            )}
          </div>
        </div>

        <div className="table-container">
          <table className="data-table till-sales-table">
            <thead>
              <tr>
                <th style={{ width: '52px' }} className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-till"
                      checked={filteredGroups.length > 0 && selectedGroups.size === filteredGroups.length}
                      onChange={toggleSelectAll}
                      disabled={filteredGroups.length === 0}
                    />
                    <label htmlFor="select-all-till" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                </th>
                <th style={{ width: '34px' }}></th>
                <th className="sortable" onClick={() => handleSort('timestamp')}>
                  Time {sortField === 'timestamp' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="sortable" onClick={() => handleSort('customerName')}>
                  Customer {sortField === 'customerName' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="text-right">Items</th>
                <th className="text-right">NET</th>
                <th className="text-right">Tax</th>
                <th className="text-right sortable" onClick={() => handleSort('totalGross')}>
                  Total {sortField === 'totalGross' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
                <th className="status-col" style={{ width: '50px' }}>Tender</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody className={loading ? 'loading-fade' : ''}>
              {filteredGroups.length === 0 && !loading ? (
                <tr>
                  <td colSpan={colCount} className="no-results">
                    {matchFilter
                      ? 'No transactions match the current filter.'
                      : transactionsOnDate > 0 && !showInvoiced
                        ? `All ${transactionsOnDate} transaction${transactionsOnDate === 1 ? '' : 's'} for this day have been invoiced — turn on Show Invoiced to see them.`
                        : transactionsOnDate > 0
                          ? 'No transactions match the current filter.'
                          : 'No transactions found. Click "Sync Day" to pull from Epos Now.'}
                  </td>
                </tr>
              ) : filteredGroups.length === 0 && loading ? (
                <tr>
                  <td colSpan={colCount}><LoadingSpinner /></td>
                </tr>
              ) : (
                sortedGroups.map(group => (
                  <React.Fragment key={group.groupKey}>
                    <tr
                      className={`expandable-row ${selectedGroups.has(group.groupKey) ? 'selected' : ''} ${group.invoiced ? 'invoiced-row' : ''} ${!group.invoiced && (group.contactMatchStatus === 'unmatched' || group.unmatchedProducts > 0) ? 'row-with-issue' : ''}`}
                      onClick={() => toggleRowExpand(group.groupKey)}
                      onContextMenu={ctxMenu.onRowContextMenu(group)}
                    >
                      {/* Checkbox */}
                      <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <div className="till-select-cell">
                          <label
                            className="tick-check-label"
                            style={{ justifyContent: 'center', gap: '0' }}
                            onMouseDown={dragSelect.onMouseDown(group.groupKey, selectedGroups.has(group.groupKey))}
                            onMouseEnter={dragSelect.onMouseEnter(group.groupKey)}
                            onClick={(e) => e.preventDefault()}
                          >
                            <input
                              type="checkbox"
                              className="tick-check-input"
                              id={`select-till-${group.groupKey}`}
                              checked={selectedGroups.has(group.groupKey)}
                              onChange={() => toggleSelectGroup(group.groupKey)}
                            />
                            <label htmlFor={`select-till-${group.groupKey}`} className="tick-check">
                              <svg width="16px" height="16px" viewBox="0 0 18 18">
                                <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                                <polyline points="1 9 7 14 15 4"></polyline>
                              </svg>
                            </label>
                          </label>
                          {/* Every row is selectable now (invoiced ones can be linked or
                              unmarked in bulk); the invoiced mark sits beside the box. */}
                          {group.invoiced && (
                            <span
                              className={`material-symbols-rounded till-invoiced-icon ${needsRepush(group) ? 'icon-warning' : ''}`}
                              title={needsRepush(group) ? `${group.invoiceNo} — not synced to Xero` : invoicedTitle(group)}
                            >
                              {group.invoicedManually ? (group.invoiceId ? 'link' : 'task_alt')
                                : String(group.invoiceStatus || '').toUpperCase() === 'DRAFT' ? 'request_quote' : 'receipt_long'}
                            </span>
                          )}
                          {/* Will go to Xero as a draft for pricing (the customer's
                              till drafts-for-pricing tick, Book to account, a
                              product in 200C/200M/200N/207/207A). */}
                          {!group.invoiced && group.draftForPricing && (
                            <span
                              className="material-symbols-rounded till-invoiced-icon"
                              title={group.draftForPricingPartial
                                ? 'Part of this sale is booked to account in a pricing account — that part becomes its own draft invoice, priced in Pricing & Costing'
                                : 'Booked to account in a pricing account — invoiced as a draft in Xero, priced in Pricing & Costing'}
                            >
                              request_quote
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Expand */}
                      <td className="expand-cell">
                        <span className={`expand-icon ${expandedRows.has(group.groupKey) ? 'expanded' : ''}`}>▼</span>
                      </td>

                      {/* Time */}
                      <td style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatTime(group.timestamp)}
                      </td>

                      {/* Customer - contact icon + name + account badge */}
                      <td>
                        <div className="customer-row">
                          {/* A customer change made here outranks the till's own
                              status: a walk-in sale moved to an account reads as that
                              account (icon and tooltip), not "Walk-in POS customer". */}
                          <span
                            className={`material-symbols-rounded contact-status-icon ${latestCustomerChange(group.customerChanges) ? 'icon-success' : getContactIconClass(group.contactMatchStatus)}`}
                            title={
                              latestCustomerChange(group.customerChanges)
                                ? `${group.matchedContactName || 'Customer'} — ${describeCustomerChange(latestCustomerChange(group.customerChanges))}`
                              : group.contactMatchStatus === 'unmatched' ? 'Epos customer not mapped — resolve in Sync Contacts'
                              : group.contactMatchStatus === 'pos_customer' ? 'Walk-in POS customer'
                              : `Matched: ${group.matchedContactName || group.customerName}`
                            }
                          >
                            {latestCustomerChange(group.customerChanges) ? 'swap_horiz' : getContactIcon(group.contactMatchStatus)}
                          </span>
                          <div className="customer-info">
                            <span className="customer-name">{group.customerName}</span>
                            {group.matchedContactName && group.matchedContactName !== group.customerName && (
                              <span className="customer-matched-name">
                                <EntityLink type="contact" id={group.accountId}>{group.matchedContactName}</EntityLink>
                              </span>
                            )}
                            {latestCustomerChange(group.customerChanges) && (
                              <span
                                className="customer-changed-note"
                                title={group.customerChanges.slice().reverse().map(c => describeCustomerChange(c)).join('\n')}
                              >
                                <span className="material-symbols-rounded">swap_horiz</span>
                                {group.invoiceNo ? `${group.invoiceNo}: ` : ''}{describeCustomerChange(latestCustomerChange(group.customerChanges))}
                              </span>
                            )}
                            {/* Booked to account and edited here since (2.19.0):
                                the invoice is the record now, not this sale. */}
                            {group.invoiceEditedHere && (
                              <span
                                className="customer-changed-note"
                                data-testid="till-edited-here"
                                title="Booked to account, so the invoice was edited in this system — it is the record now, and no longer matches this till sale"
                              >
                                <span className="material-symbols-rounded">edit_note</span>
                                {group.invoiceNo ? `${group.invoiceNo}: ` : ''}edited here — no longer matches this sale
                              </span>
                            )}
                          </div>
                          {group.accountNo && ((group.contactMatchStatus !== 'pos_customer' && group.contactMatchStatus !== 'unmatched')
                            || (group.customerChanges || []).length > 0) && (
                            <span className="account-badge-inline">
                              <EntityLink type="contact" id={group.accountId} variant="badge">{group.accountNo}</EntityLink>
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Items */}
                      <td className="text-right">
                        {group.totalItems}
                        {group.unmatchedProducts > 0 && (
                          <span className="material-symbols-rounded icon-warning" style={{ fontSize: "16px", marginLeft: 3, verticalAlign: 'middle' }} title={`${group.unmatchedProducts} product(s) not matched to inventory`}>warning</span>
                        )}
                      </td>

                      {/* NET */}
                      <td className="text-right currency">{formatCurrency(group.totalNet)}</td>

                      {/* Tax */}
                      <td className="text-right currency">{formatCurrency(group.totalTax)}</td>

                      {/* Total */}
                      <td className="text-right currency" style={{ fontWeight: 600 }}>{formatCurrency(group.totalGross)}</td>

                      {/* Tender icons — one per tender used (mixed sales show several),
                          plus a Not-synced pill when the invoice never reached Xero */}
                      <td className="status-col">
                        {(() => {
                          const parts = getTenderParts(group);
                          return (
                            <div className="tender-icons" title={getTenderTitle(parts)}>
                              {parts.map(part => (
                                <span
                                  key={part.name}
                                  className={`material-symbols-rounded tender-icon tender-icon-${(part.name || '').toLowerCase().trim().replace(/\s+/g, '-')}`}
                                >
                                  {getTenderIcon(part.name)}
                                </span>
                              ))}
                            </div>
                          );
                        })()}
                        {group.invoiced && group.invoicedManually && (
                          <span className="status-pill status-marked-invoiced" title={invoicedTitle(group)}>
                            Marked
                          </span>
                        )}
                        {needsRepush(group) && (
                          <span
                            className="status-pill status-not-synced"
                            title={group.xeroPushError || 'Created locally but never pushed to Xero'}
                          >
                            Not synced
                          </span>
                        )}
                      </td>

                      {/* Actions — one menu, the same list the right-click menu shows */}
                      <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                        <div className="actions-cell">
                          <RowActionsMenu actions={buildRowActions(group)} />
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Line Items */}
                    <tr className={`expanded-content ${expandedRows.has(group.groupKey) ? 'expanded' : ''}`}>
                      <td colSpan={colCount}>
                        <div className="line-items-container">
                          {(groupLineItems[group.groupKey] || []).length > 0 ? (
                            <table className="line-items-table">
                              <thead>
                                <tr>
                                  <th style={{ width: "33%" }}>Product</th>
                                  <th className="text-right">Qty</th>
                                  <th className="text-right">Unit</th>
                                  <th className="text-right">Tax</th>
                                  <th className="text-right">Discount</th>
                                  <th className="text-right">NET</th>
                                  <th className="text-right">Total</th>
                                  <th>Tax Code</th>
                                  <th>Account</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupLineItems[group.groupKey].map((item, idx) => (
                                  <tr
                                    key={idx}
                                    className={!item.sales_account_code ? 'line-item-unmatched' : ''}
                                    onClick={!item.sales_account_code ? (e) => { e.stopPropagation(); openProductLinker(item); } : undefined}
                                    title={!item.sales_account_code ? 'Product not matched — click to link to inventory' : undefined}
                                    style={!item.sales_account_code ? { cursor: 'pointer' } : undefined}
                                  >
                                    <td>
                                      <div className="product-name-row">
                                        <span>{item.product_name}</span>
                                        {item.inventory_item_code && (
                                          <span className="account-badge-inline" style={{ fontSize: '10px', padding: '1px 5px' }}>
                                            <EntityLink type="inventory_item" id={item.inventory_item_id} variant="badge">{item.inventory_item_code}</EntityLink>
                                          </span>
                                        )}
                                        {item.manual_inventory_link && item.inventory_item_code !== String(item.epos_product_id) && (
                                          <button className="btn-icon-inline" onClick={(e) => { e.stopPropagation(); unlinkProduct(item.epos_product_id); }} title="Unlink product">
                                            <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>link_off</span>
                                          </button>
                                        )}
                                        {!item.sales_account_code && (
                                          <span className="material-symbols-rounded icon-warning" style={{ fontSize: '16px' }}>warning</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="text-right">{formatQuantity(item.quantity || 0)}</td>
                                    <td className="text-right">{formatPrice(item.unit_price_exc_tax || 0)}</td>
                                    <td className="text-right">{formatCurrency(item.tax_amount)}</td>
                                    <td
                                      className="text-right"
                                      title={item.discount_reason_name || ''}
                                    >
                                      {parseFloat(item.discount_amount) !== 0 && (
                                        <>
                                          {formatCurrency(Math.abs(parseFloat(item.discount_amount)))}
                                          {item.discount_reason_name && (
                                            <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                                              {item.discount_reason_name}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </td>
                                    <td className="text-right">{formatCurrency(item.net_sales)}</td>
                                    <td className="text-right">{formatCurrency(item.total_sales)}</td>
                                    <td>{item.tax_code || '-'}</td>
                                    <td>{item.sales_account_code || <span className="unmatched-account" style={{ fontWeight: '600', fontSize: '0.9em', letterSpacing: '0.5px', fontStyle: 'normal' }}>UNMATCHED</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : expandedRows.has(group.groupKey) ? (
                            <div className="loading-lines">Loading line items...</div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Detail Modal */}
      {viewingGroup && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setViewingGroup(null)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: '0' }}>Transaction Detail</h3>
              <button className="btn-icon" onClick={() => setViewingGroup(null)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "var(--spacing-xl)" }}>
              {/* Header info */}
              <div style={{ borderRadius: 'var(--radius-lg)', background: 'var(--bg-main)', padding: 'var(--spacing-lg', display: 'grid', gridTemplateColumns: '1fr 1fr 0.5fr 0.5fr', gap: '12px', marginTop: '-15px', marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-sm)', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)', textTransform: 'uppercase' }}>Customer</span>
                  <div style={{ fontWeight: 500 }}>{viewingGroup.customerName}</div>
                  {viewingGroup.matchedContactName && viewingGroup.matchedContactName !== viewingGroup.customerName && (
                    <div style={{ color: 'var(--color-success)', fontSize: 'var(--font-xs)' }}>
                      <span className="material-symbols-rounded" style={{ fontSize: 12 }}>check_circle</span>{' '}
                      <EntityLink type="contact" id={viewingGroup.accountId}>{viewingGroup.matchedContactName}</EntityLink>
                      {viewingGroup.accountNo && (
                        <span className="account-badge-inline" style={{ marginLeft: 6 }}>
                          <EntityLink type="contact" id={viewingGroup.accountId} variant="badge">{viewingGroup.accountNo}</EntityLink>
                        </span>
                      )}
                    </div>
                  )}
                  {viewingGroup.contactMatchStatus === 'unmatched' && (
                    <div style={{ color: 'var(--color-warning)', fontSize: 'var(--font-xs)' }}>
                      <span className="material-symbols-rounded" style={{ fontSize: 12 }}>warning</span> Contact not matched
                    </div>
                  )}
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)', textTransform: 'uppercase' }}>Date / Time</span>
                  <div>{new Date(viewingGroup.timestamp).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} {formatTime(viewingGroup.timestamp)}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)', textTransform: 'uppercase' }}>Tender</span>
                  <div>{viewingGroup.tenderSummary}</div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)', textTransform: 'uppercase' }}>Total</span>
                  <div style={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(viewingGroup.totalGross)}</div>
                </div>
                {viewingGroup.invoiced && viewingGroup.invoiceNo && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-xs)', textTransform: 'uppercase' }}>Invoice</span>
                    <div>
                      <button
                        type="button"
                        className="account-badge-inline badge-link"
                        onClick={() => viewingGroup.invoiceId && setViewingInvoiceId(viewingGroup.invoiceId)}
                        disabled={!viewingGroup.invoiceId}
                        title={viewingGroup.invoiceId ? `View ${viewingGroup.invoiceNo}` : 'Invoice not available'}
                      >
                        {viewingGroup.invoiceNo}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Line items */}
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Unit</th>
                    <th className="text-right">Discount</th>
                    <th className="text-right">Tax</th>
                    <th className="text-right">NET</th>
                    <th>Tax Code</th>
                    <th>Account</th>
                  </tr>
                </thead>
                <tbody>
                  {viewingLineItems.length > 0 ? viewingLineItems.map((item, idx) => (
                    <tr key={idx} style={!item.sales_account_code ? { background: 'rgba(245, 158, 11, 0.08)' } : {}}>
                      <td>
                        <div className="product-name-row">
                          <span>{item.product_name}</span>
                          {item.inventory_item_code && (
                            <span className="account-badge-inline" style={{ fontSize: '10px', padding: '1px 5px' }}>
                              <EntityLink type="inventory_item" id={item.inventory_item_id} variant="badge">{item.inventory_item_code}</EntityLink>
                            </span>
                          )}
                          {item.manual_inventory_link && item.inventory_item_code !== String(item.epos_product_id) && (
                            <button className="btn-icon-inline" onClick={() => unlinkProduct(item.epos_product_id)} title="Unlink product">
                              <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>link_off</span>
                            </button>
                          )}
                          {!item.sales_account_code && (
                            <button className="btn-icon-inline icon-warning" onClick={() => openProductLinker(item)} title="Click to link to inventory">
                              <span className="material-symbols-rounded icon-warning" style={{ fontSize: '16px' }}>warning</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-right">{formatQuantity(item.quantity || 0)}</td>
                      <td className="text-right">{formatPrice(item.unit_price_exc_tax || 0)}</td>
                      <td className="text-right">{parseFloat(item.discount_amount) !== 0 ? formatCurrency(Math.abs(parseFloat(item.discount_amount))) : ''}</td>
                      <td className="text-right">{formatCurrency(item.tax_amount)}</td>
                      <td className="text-right">{formatCurrency(item.net_sales)}</td>
                      <td>{item.tax_code || '-'}</td>
                      <td>{item.sales_account_code || <button className="btn-link unmatched-account" onClick={() => openProductLinker(item)}>link</button>}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" className="text-center" style={{ padding: 'var(--spacing-lg)', color: 'var(--text-muted)' }}>Loading...</td></tr>
                  )}
                </tbody>
              </table>

              {/* Totals row */}
              <div style={{ alignSelf: 'end', textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 22px 8px 12px', justifyContent: 'end', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginTop: 'var(--spacing-md)', fontSize: 'var(--font-sm)' }}>
                <div><span style={{ color: 'var(--text-muted)' }}>NET:</span> {formatCurrency(viewingGroup.totalNet)}</div>
                <div><span style={{ color: 'var(--text-muted)' }}>Tax:</span> {formatCurrency(viewingGroup.totalTax)}</div>
                <div style={{ fontWeight: 600 }}>Total: {formatCurrency(viewingGroup.totalGross)}</div>
              </div>

            </div>

            {/* Both buttons below are conditional, and Close — which was not —
                has gone to the header. The union of the two conditions guards
                the footer so a matched, already-invoiced transaction doesn't
                get an empty bar. */}
            {(viewingGroup.contactMatchStatus === 'unmatched' || !viewingGroup.invoiced) && (
            <div className="modal-footer">
              {viewingGroup.contactMatchStatus === 'unmatched' && (
                <button className="btn-slideout" onClick={() => { setViewingGroup(null); goToSyncContacts(); }}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">person_search</span>
                  </div>
                  <span className="btn-label">Resolve in Sync Contacts</span>
                </button>
              )}
              {!viewingGroup.invoiced && viewingGroup.contactMatchStatus !== 'unmatched' && (
                <button className="btn-slideout" onClick={() => { setViewingGroup(null); generateSingleInvoice(viewingGroup); }} disabled={generating}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">receipt</span>
                  </div>
                  <span className="btn-label">Generate Invoice</span>
                </button>
              )}
            </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Product-Inventory Link Modal */}
      {linkingProduct && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => { if (!savingNewItem) { setLinkingProduct(null); setCreatingItem(false); setNewItemForm(null); } }}>
          <div className="modal-content resolve-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: creatingItem ? '560px' : '500px' }}>
            <div className="modal-header">
              <h3>{creatingItem ? 'Create Inventory Item' : 'Link Product to Inventory'}</h3>
              <button className="btn-close" onClick={() => { if (!savingNewItem) { setLinkingProduct(null); setCreatingItem(false); setNewItemForm(null); } }}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body">
              <p className="resolve-customer-name">
                Epos Now product: <strong>{linkingProduct.productName}</strong>
                <br />
                <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)' }}>Product ID: {linkingProduct.eposProductId}</span>
              </p>

              {!creatingItem ? (
                <>
                  <div className="search-section">
                    <h4>Select Inventory Item</h4>
                    <SearchableDropdown
                      value=""
                      placeholder="Search inventory by name or code..."
                      searchPlaceholder="Type to filter..."
                      onChange={(val) => { if (val) linkProductToInventory(parseInt(val)); }}
                      options={inventoryResults.map(item => ({
                        value: item.id.toString(),
                        label: item.item_name,
                        badge: item.item_code || '',
                        searchText: `${item.item_code || ''} ${item.item_name} ${item.sales_account_code || ''}`
                      }))}
                    />
                  </div>
                  <div className="modal-footer">
                    <div className="buttons slideout" style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 'var(--spacing-sm', justifyContent: 'flex-end' }}>
                      <button className="btn-slideout" onClick={() => openCreateItemForm(linkingProduct.eposProductId)}>
                        <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                        <span className="btn-label">Create New Inventory Item</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : newItemForm ? (
                <div className="create-item-form">
                  {(newItemForm.categoryName || newItemForm.eposSupplierName) && (
                    <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', marginBottom: 'var(--spacing-md)', padding: '6px 8px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)' }}>
                      {newItemForm.categoryName && (
                        <div>
                          Category: <strong>{newItemForm.parentCategoryName ? `${newItemForm.parentCategoryName} > ` : ''}{newItemForm.categoryName}</strong>
                          {' — '}
                          account {newItemForm.accountCodeMatched ? <strong>{newItemForm.sales_account_code}</strong> : <em style={{ color: 'var(--color-warning)' }}>unmapped, defaulted to {newItemForm.sales_account_code}</em>}
                          {' · '}
                          tax {newItemForm.taxGroupMatched ? <strong>{newItemForm.sales_tax_type}</strong> : <em style={{ color: 'var(--color-warning)' }}>from Epos tax group</em>}
                        </div>
                      )}
                      {newItemForm.eposSupplierName && (
                        <div>Epos supplier: <strong>{newItemForm.eposSupplierName}</strong></div>
                      )}
                    </div>
                  )}
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Item Code <span style={{ color: 'var(--color-error)' }}>*</span></label>
                      <input type="text" className="search-input" value={newItemForm.item_code} onChange={e => setNewItemForm(f => ({ ...f, item_code: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Item Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                      <input type="text" className="search-input" value={newItemForm.item_name} onChange={e => setNewItemForm(f => ({ ...f, item_name: e.target.value }))} />
                    </div>
                    <div className="form-group">
                      <label>Sales Description</label>
                      <input type="text" className="search-input" value={newItemForm.description || ''} onChange={e => setNewItemForm(f => ({ ...f, description: e.target.value }))} />
                    </div>

                    {/* Toggles */}
                    <div className="detail-row checkbox-toggles" style={{ display: 'flex', gap: 'var(--spacing-md)', margin: 'var(--spacing-md) 0', fontSize: '0.65em', textTransform: 'uppercase', fontWeight: '600', color: 'var(--text-muted)', justifyContent: 'space-between' }}>
                      <label className="tick-check-label">
                        <input type="checkbox" className="tick-check-input" id="till-item-is-sold"
                          checked={!!newItemForm.is_sold}
                          onChange={e => setNewItemForm(f => ({ ...f, is_sold: e.target.checked }))} />
                        <label htmlFor="till-item-is-sold" className="tick-check">
                          <svg width="18px" height="18px" viewBox="0 0 18 18">
                            <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                            <polyline points="1 9 7 14 15 4"></polyline>
                          </svg>
                        </label>
                        <span>Is Sold</span>
                      </label>
                      <label className="tick-check-label">
                        <input type="checkbox" className="tick-check-input" id="till-item-is-purchased"
                          checked={!!newItemForm.is_purchased}
                          onChange={e => setNewItemForm(f => ({ ...f, is_purchased: e.target.checked }))} />
                        <label htmlFor="till-item-is-purchased" className="tick-check">
                          <svg width="18px" height="18px" viewBox="0 0 18 18">
                            <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                            <polyline points="1 9 7 14 15 4"></polyline>
                          </svg>
                        </label>
                        <span>Is Purchased</span>
                      </label>
                      <label className="tick-check-label">
                        <input type="checkbox" className="tick-check-input" id="till-item-is-tracked"
                          checked={!!newItemForm.is_tracked_as_inventory}
                          onChange={e => setNewItemForm(f => ({ ...f, is_tracked_as_inventory: e.target.checked }))} />
                        <label htmlFor="till-item-is-tracked" className="tick-check">
                          <svg width="18px" height="18px" viewBox="0 0 18 18">
                            <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                            <polyline points="1 9 7 14 15 4"></polyline>
                          </svg>
                        </label>
                        <span>Track as Inventory</span>
                      </label>
                    </div>

                    {/* Sales section */}
                    <div className={`detail-row sales ${!newItemForm.is_sold ? 'disabled-section' : ''}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                      <div className="form-group">
                        <label>Sales Price</label>
                        <Input type="number" step="0.01"
                          value={newItemForm.sales_unit_price || ''}
                          onChange={e => setNewItemForm(f => ({ ...f, sales_unit_price: e.target.value }))}
                          placeholder="0.00" disabled={!newItemForm.is_sold} />
                      </div>
                      <div className="form-group">
                        <label>Sales Tax Type</label>
                        <Dropdown
                          value={newItemForm.sales_tax_type || ''}
                          onChange={(value) => setNewItemForm(f => ({ ...f, sales_tax_type: value }))}
                          options={SALES_TAX_OPTIONS}
                          placeholder="Select tax type"
                          disabled={!newItemForm.is_sold}
                        />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / 3' }}>
                        <label>Sales Account</label>
                        <Dropdown
                          value={newItemForm.sales_account_code || ''}
                          onChange={(value) => setNewItemForm(f => ({ ...f, sales_account_code: value }))}
                          options={[
                            { value: '', label: 'None' },
                            ...salesAccounts.map(a => ({
                              value: a.code,
                              label: (a.name || ACCOUNT_NAMES[a.code]) ? `${a.code} — ${a.name || ACCOUNT_NAMES[a.code]}` : a.code
                            }))
                          ]}
                          placeholder="Select account"
                          disabled={!newItemForm.is_sold}
                        />
                      </div>
                    </div>

                    {/* Purchase section */}
                    <div className={`detail-row purchases ${!newItemForm.is_purchased ? 'disabled-section' : ''}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr ', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                      <div className="form-group">
                        <label>Purchase Price</label>
                        <Input type="number" step="0.01"
                          value={newItemForm.purchase_unit_price || ''}
                          onChange={e => setNewItemForm(f => ({ ...f, purchase_unit_price: e.target.value }))}
                          placeholder="0.00" disabled={!newItemForm.is_purchased} />
                      </div>
                      <div className="form-group">
                        <label>Purchase Tax Type</label>
                        <Dropdown
                          value={newItemForm.purchase_tax_type || ''}
                          onChange={(value) => setNewItemForm(f => ({ ...f, purchase_tax_type: value }))}
                          options={PURCHASE_TAX_OPTIONS}
                          placeholder="Select tax type"
                          disabled={!newItemForm.is_purchased}
                        />
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / 3' }}>
                        <label>Purchase Account</label>
                        <Dropdown
                          value={newItemForm.purchase_account_code || ''}
                          onChange={(value) => setNewItemForm(f => ({ ...f, purchase_account_code: value }))}
                          options={[
                            { value: '', label: 'None' },
                            ...purchaseAccounts.map(a => ({
                              value: a.code,
                              label: (a.name || ACCOUNT_NAMES[a.code]) ? `${a.code} — ${a.name || ACCOUNT_NAMES[a.code]}` : a.code
                            }))
                          ]}
                          placeholder="Select account"
                          disabled={!newItemForm.is_purchased}
                        />
                      </div>
                    </div>

                    {/* Tracked inventory section */}
                    {newItemForm.is_tracked_as_inventory && (
                      <div className="detail-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--spacing-md)', marginTop: 'var(--spacing-sm)' }}>
                        <div className="form-group">
                          <label>COGS Account Code</label>
                          <Input type="text"
                            value={newItemForm.cogs_account_code || ''}
                            onChange={e => setNewItemForm(f => ({ ...f, cogs_account_code: e.target.value }))}
                            placeholder="e.g. 310" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <div className="buttons slideout" style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 'var(--spacing-sm', justifyContent: 'space-between' }}>
                      <div>
                        <button className="btn-slideout" onClick={() => { setCreatingItem(false); setNewItemForm(null); recalcSlideouts(); }}>
                          <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_back</span></div>
                          <span className="btn-label">Back to Search</span>
                        </button>
                      </div>
                      <div>
                        <button className="btn-slideout" onClick={saveNewItemAndLink} disabled={savingNewItem}>
                          <div className="icon-wrapper"><span className="material-symbols-rounded primary">{savingNewItem ? 'progress_activity' : 'add_link'}</span></div>
                          <span className="btn-label">{savingNewItem ? 'Creating...' : 'Create & Link'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Right-click: the row's own menu, or — on a row inside a multi-row
          selection — the bulk actions as well. */}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={buildBulkActions}
      />

      {invoiceAction && (
        <TillInvoiceActionModal
          mode={invoiceAction.mode}
          groups={invoiceAction.groups}
          onClose={() => setInvoiceAction(null)}
          onDone={onInvoiceActionDone}
        />
      )}

      {historyGroup && (
        <AuditHistoryModal
          isOpen
          onClose={() => setHistoryGroup(null)}
          entityType="till_transaction"
          entityId={historyGroup.transactionIds[0]}
          entityIdentifier={`${historyGroup.customerName} · ${formatTime(historyGroup.timestamp)}`}
        />
      )}

      {viewingInvoiceId && (
        <InvoiceModal
          invoiceId={viewingInvoiceId}
          onClose={() => { setViewingInvoiceId(null); loadTransactions(); }}
          onOpenInSearch={openInSearchInvoices}
        />
      )}

      {changingCustomerInvoiceId && (
        <ChangeCustomerModal
          invoiceId={changingCustomerInvoiceId}
          onClose={() => { setChangingCustomerInvoiceId(null); loadTransactions(); }}
          onDone={(result) => {
            setChangingCustomerInvoiceId(null);
            loadTransactions();
            setAlertDialog({ isOpen: true, type: 'success',
              message: `${result.invoice?.invoice_no || 'The invoice'}: ${describeCustomerChange(result.change)}.` });
          }}
        />
      )}

      {priorMonth.dialog}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />
    </div>
  );
}

export default TillSales;
